/**
 * @fileoverview The top of the Seasons tab: which season this is and what it has done,
 * what is ahead, and the page a new gardener sees before anything has
 * happened. Every word and decision here is the backend's (the season
 * brief, GET /season); this lays it out.
 *
 * Seasons was a diary reached from the account menu. Once it took a slot in
 * the tab bar it needed a reason to be opened on purpose, not only to look
 * back: a headline for the season, and the two or three dates coming up.
 *
 * The header is the season's name and one sentence under it, built by the
 * backend only from what was recorded: the plants and beds, then a clause
 * for each milestone reached (the first sown, the first transplanted, the
 * first harvest), each a button to its month. A milestone not reached is
 * simply not mentioned; the sentence never says "no harvest", because a
 * harvest is only known when a note says so.
 *
 * Recent and coming up is the sky's calendar as the brief sends it, in date order:
 * the frost and freeze ranges as Today says them (each leading to that
 * season's frost dates on Weather; frost and freeze in the colours the brief
 * sends with each line, cool for worth knowing and cold for time to act, the
 * pair the verdict chips use, with what each does to a plant on its own line), the
 * planting lines ("Feb 11 · Start tomatoes indoors", each leading to those
 * plants on Plants; one open now says until when), the next new and full
 * moons, and the next equinox or solstice. Its frost and freeze icons are
 * the ones the plant cards and the diary's frost rows use (D58).
 *
 * Where: Your Seasons (SeasonPage): SeasonHeader or SeasonEmpty at the top,
 * WhatsAhead under the review entry.
 * API: none of its own; the brief comes in (useSeason).
 * Words (en/es): season.ahead.title, season.emptyLede, season.emptyBody,
 * season.addPlant, season.takePhoto; everything else is the brief's.
 *
 * @module SeasonHeader
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, Plus, Snowflake, Sprout, Sun, ThermometerSnowflake } from 'lucide-react';
import { cn } from '@/lib/utils';
import { conditionTextClass } from '@/shared/utils/outlookSeverity';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { Button } from '@/shared/components/ui/button';
import { MoonDisc } from '@/shared/components/icons/MoonDisc';
import type { SeasonAheadLine, SeasonHeaderBrief, SeasonSaid } from '@/shared/api/season';

/** The frame each of the three sections sits in. */
const CARD = 'rounded-lg border bg-card p-4';

/** The season, and what the garden did in it. */
interface SeasonHeaderProps {
  /** The season's name and its one sentence (the brief's header). */
  header: SeasonHeaderBrief;
  /** Opens the month a milestone happened in, so its day is on screen. */
  onMonth: (month: string) => void;
}

/**
 * The season's name and one sentence of what it has done, each milestone a link to its month.
 *
 * @param props - SeasonHeaderProps.
 */
export const SeasonHeader: React.FC<SeasonHeaderProps> = ({ header, onMonth }) => (
  <section className={cn(CARD, 'mt-3')} aria-label={header.title.text}>
    <h2 className="text-lg font-semibold font-body">{header.title.text}</h2>
    <p className="text-sm text-muted-foreground">
      {header.counts.text}
      {header.milestones.map((milestone) => (
        <React.Fragment key={milestone.key}>
          {milestone.joiner}
          <button
            type="button"
            onClick={() => onMonth(milestone.month)}
            className="rounded-sm text-foreground underline decoration-1 underline-offset-4 hover:decoration-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {milestone.text}
          </button>
        </React.Fragment>
      ))}
      {'.'}
      {header.quiet && ` ${header.quiet.text}`}
    </p>
  </section>
);

/** A frost or freeze line's words as a link, underlined like the milestones above. */
const RANGE_LINK = 'rounded-sm underline decoration-1 underline-offset-4 hover:decoration-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Recent and coming up's lines. */
interface WhatsAheadProps {
  /** The brief's ahead: the ranges, the planting lines and the sky, in date order. */
  ahead: SeasonAheadLine[];
}

/**
 * One line of Recent and coming up: the day beside it, its words, and the small line under.
 *
 * @param props.line - The line.
 */
const AheadLine: React.FC<{ line: SeasonAheadLine }> = ({ line }) => {
  if (line.kind === 'frost' || line.kind === 'freeze') {
    /** The line's icon, as the plant cards (card/Verdict) and the diary's frost rows draw it: a cold thermometer for frost, a snowflake for a freeze. */
    const Icon = line.kind === 'frost' ? ThermometerSnowflake : Snowflake;
    return (
      <li className="flex items-baseline gap-3">
        <span className="w-14 shrink-0 tabular-nums text-muted-foreground">{line.dateText}</span>
        <span className="min-w-0">
          <span className={cn('flex items-start gap-1.5 font-medium', conditionTextClass(line.token))}>
            <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {line.link ? <Link to={line.link} className={RANGE_LINK}>{line.text}</Link> : line.text}
          </span>
          {line.sub && <span className="block text-xs text-muted-foreground">{line.sub}</span>}
        </span>
      </li>
    );
  }
  if (line.kind === 'planting') {
    return (
      <li className="flex items-baseline gap-3">
        <span className="w-14 shrink-0 tabular-nums text-muted-foreground">{line.dateText}</span>
        <span className="min-w-0">
          <span className="flex items-start gap-1.5 font-medium text-primary">
            <Sprout className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {/* Those plants on Plants, as Coming up's planting lines open them. */}
            {line.link ? <Link to={line.link} className={RANGE_LINK}>{line.text}</Link> : line.text}
          </span>
          {line.until && <span className="block text-xs text-muted-foreground">{line.until.text}</span>}
        </span>
      </li>
    );
  }
  return (
    <li className="flex items-baseline gap-3">
      <span className="w-14 shrink-0 tabular-nums text-muted-foreground">{line.dateText}</span>
      <span className="flex items-center gap-1.5">
        {line.kind === 'moon'
          ? <MoonDisc shape={line.phase ?? 'new'} className="h-4 w-4 shrink-0 text-muted-foreground" />
          : <Sun className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
        {line.text}
      </span>
    </li>
  );
};

/**
 * The dates coming: the frost and freeze ranges, the planting lines, the
 * next moons and the next turn of the year, as the brief orders them.
 * Nothing at all when there is nothing to say.
 *
 * @param props - WhatsAheadProps.
 */
export const WhatsAhead: React.FC<WhatsAheadProps> = ({ ahead }) => {
  const { reactTranslate: t } = useTranslation();
  if (ahead.length === 0) return null;
  return (
    <section className={cn(CARD, 'mt-3')} aria-label={t('season.ahead.title')}>
      <h2 className="font-body mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('season.ahead.title')}</h2>
      <ul className="space-y-1.5 text-sm">
        {ahead.map((line) => <AheadLine key={line.key} line={line} />)}
      </ul>
    </section>
  );
};

/** The season, and the two ways to begin it. */
interface SeasonEmptyProps {
  /** The season's name ("Fall 2026"). */
  title: SeasonSaid;
  /** Opens the form to add a plant. */
  onAddPlant: () => void;
  /** Opens the camera, or the photo picker. */
  onTakePhoto: () => void;
}

/**
 * Day one: what will appear here, and the two ways to make the first entry.
 *
 * @param props - SeasonEmptyProps.
 */
export const SeasonEmpty: React.FC<SeasonEmptyProps> = ({ title, onAddPlant, onTakePhoto }) => {
  const { reactTranslate: t } = useTranslation();
  return (
    <section className={cn(CARD, 'mt-3')}>
      <h2 className="text-lg font-semibold font-body">{title.text}</h2>
      <p className="text-sm text-muted-foreground">{t('season.emptyLede')}</p>
      <p className="mt-3 text-sm">{t('season.emptyBody')}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={onAddPlant}><Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />{t('season.addPlant')}</Button>
        <Button variant="outline" onClick={onTakePhoto}><Camera className="mr-1.5 h-4 w-4" aria-hidden="true" />{t('season.takePhoto')}</Button>
      </div>
    </section>
  );
};
